/* Studio draft preview — renders a case-study draft using the studio-authed
   preview endpoint. Draft media URLs are short-lived (15-min) presigned R2
   URLs resolved server-side. Never exposed publicly.                      */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Film, ArrowLeft, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const studioHeaders = () => {
    const pwd = (typeof window !== "undefined" && window.localStorage.getItem("slowcafe-studio-pwd")) || "";
    return pwd ? { "X-Studio-Password": pwd } : {};
};

export default function CaseStudyDraftPreview() {
    const { slug } = useParams();
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/studio/projects/${slug}/preview`,
                                             { headers: studioHeaders() });
            setDoc(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "无法加载草稿");
            if (e?.response?.status === 401) toast.error("请先登入 Studio");
        }
    };
    useEffect(() => { load(); }, [slug]);

    if (err) return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-cafe-cream text-cafe-espresso">
            <div className="text-center max-w-md">
                <Lock className="mx-auto h-10 w-10 text-cafe-olive" />
                <h1 className="mt-4 font-serif text-2xl">草稿预览需要 Studio 登录</h1>
                <p className="mt-2 text-sm text-cafe-mocha">{err}</p>
                <Link to="/studio" className="mt-6 inline-flex items-center gap-1 text-cafe-olive hover:underline">
                    <ArrowLeft className="h-4 w-4" /> 返回 Studio
                </Link>
            </div>
        </div>
    );
    if (!doc) return <div className="min-h-screen flex items-center justify-center text-cafe-mocha">加载中…</div>;

    const videoSrc = doc.video_file_url_resolved || doc.video_url || "";
    const posterSrc = doc.video_poster_resolved || "";
    const heroSrc = doc.hero_image_resolved || doc.hero_image || "";
    const gallery = (doc.gallery_resolved || doc.gallery || []).filter(Boolean);

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso pb-20" data-testid="draft-preview-page">
            <DraftNoIndex title={doc.title} />
            <div className="sticky top-0 z-40 bg-amber-100 border-b border-amber-300 px-4 py-2 text-xs text-amber-900 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5" />
                    <span>草稿预览 · 仅 Studio 可见 · 15 分钟签名 URL · 公开 /work 不会显示此条目</span>
                </div>
                <Link to="/studio" className="hover:underline" data-testid="draft-back">返回 Studio</Link>
            </div>

            <section className="max-w-5xl mx-auto px-6 pt-10">
                <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive">{doc.category || "Case study"}</div>
                <h1 className="mt-2 font-serif text-4xl md:text-5xl">{doc.title}</h1>
                {doc.client_name && <div className="mt-2 text-sm text-cafe-mocha">Client · {doc.client_name}</div>}
            </section>

            {/* Hero Film */}
            {videoSrc && (
                <section className="max-w-5xl mx-auto px-6 mt-8" data-testid="draft-hero-film">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cafe-olive mb-3">
                        <Film className="h-3.5 w-3.5" /> Hero Film
                    </div>
                    <video controls playsInline preload="metadata"
                           src={videoSrc} poster={posterSrc || undefined}
                           data-testid="draft-video"
                           className="w-full rounded-2xl bg-black" />
                </section>
            )}

            {/* One-line concept / description */}
            {doc.description && (
                <section className="max-w-3xl mx-auto px-6 mt-10">
                    <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive mb-2">One-line concept</div>
                    <p className="text-lg leading-relaxed">{doc.description}</p>
                </section>
            )}

            {/* Brand Objective */}
            {doc.challenge && (
                <section className="max-w-3xl mx-auto px-6 mt-10">
                    <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive mb-2">Brand Objective</div>
                    <p className="text-sm leading-relaxed text-cafe-mocha whitespace-pre-wrap">{doc.challenge}</p>
                </section>
            )}

            {/* Creative Direction */}
            {doc.creative_direction && (
                <section className="max-w-3xl mx-auto px-6 mt-10">
                    <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive mb-2">Creative Direction</div>
                    <p className="text-sm leading-relaxed text-cafe-mocha whitespace-pre-wrap">{doc.creative_direction}</p>
                </section>
            )}

            {/* Hero Stills */}
            {(heroSrc || gallery.length > 0) && (
                <section className="max-w-5xl mx-auto px-6 mt-12">
                    <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive mb-3">Hero Stills</div>
                    {heroSrc && <img src={heroSrc} alt="hero" className="w-full rounded-2xl mb-3" data-testid="draft-hero-image" />}
                    {gallery.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {gallery.map((u, i) => (
                                <img key={i} src={u} alt={`still ${i + 1}`} className="w-full aspect-square object-cover rounded-xl" data-testid={`draft-gallery-${i}`} />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Your Role / Services */}
            {(doc.services || []).length > 0 && (
                <section className="max-w-3xl mx-auto px-6 mt-12">
                    <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive mb-2">Your Role</div>
                    <div className="flex flex-wrap gap-2">
                        {doc.services.map((s) => (
                            <span key={s} className="text-xs px-3 py-1 rounded-full bg-cafe-latte border border-border">{s}</span>
                        ))}
                    </div>
                </section>
            )}

            {/* Deliverables */}
            {(doc.deliverables || []).length > 0 && (
                <section className="max-w-3xl mx-auto px-6 mt-10">
                    <div className="text-xs uppercase tracking-[0.2em] text-cafe-olive mb-2">Deliverables</div>
                    <ul className="list-disc list-inside text-sm text-cafe-mocha space-y-1">
                        {doc.deliverables.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                </section>
            )}

            {/* CTA — reuse existing Campaign / Custom Quote entries, no fabricated numbers */}
            <section className="max-w-3xl mx-auto px-6 mt-14 pt-10 border-t border-border">
                <div className="text-center">
                    <h2 className="font-serif text-2xl mb-4">Discuss your campaign</h2>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <Link to="/#contact" className="rounded-full bg-cafe-olive text-cafe-cream px-6 py-2.5 text-sm font-medium hover:bg-cafe-oliveDark">
                            Discuss a Campaign
                        </Link>
                        <Link to="/photo-video-services#services-pricing" className="rounded-full border border-cafe-espresso/25 bg-cafe-cream text-cafe-espresso px-6 py-2.5 text-sm font-medium hover:bg-cafe-latte">
                            Fixed Packages
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function DraftNoIndex({ title }) {
    useEffect(() => {
        const prev = document.title;
        document.title = `Draft preview — ${title || "Untitled"} — Kaily Studio`;
        const ensure = (sel, make) => {
            let el = document.head.querySelector(sel);
            if (!el) { el = make(); document.head.appendChild(el); }
            return el;
        };
        const robots = ensure('meta[name="robots"]', () => {
            const m = document.createElement("meta");
            m.setAttribute("name", "robots");
            return m;
        });
        const prevRobots = robots.getAttribute("content") || "";
        robots.setAttribute("content", "noindex, nofollow");
        return () => {
            document.title = prev;
            robots.setAttribute("content", prevRobots || "index, follow");
        };
    }, [title]);
    return null;
}
